'use client';

import Link from 'next/link';
import {
  BrainCircuit,
  Film,
  Languages,
  Target,
  Workflow,
  Zap
} from 'lucide-react';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';

import { trackCta } from '~/lib/track-cta';

const topCards = [
  {
    icon: Film,
    title: 'From Recording to Finished Video',
    desc: 'Start with a screen recording and turn it into a complete video with script, voiceover, captions, and final edits in one place.'
  },
  {
    icon: BrainCircuit,
    title: 'AI That Understands Your Content',
    desc: 'VideoMule analyzes what\u2019s happening in your video and builds a structured script that matches your steps and screen flow.'
  },
  {
    icon: Zap,
    title: 'Fast Editing Without Heavy Software',
    desc: 'Trim clips, add zoom, adjust layouts, manage captions, and refine timing without learning complicated editing tools.'
  }
];

const bottomCards = [
  {
    icon: Languages,
    title: 'Create in Multiple Languages',
    desc: 'Generate voiceovers and captions in multiple languages from the same recording, making it easy to localize content without re-recording.'
  },
  {
    icon: Workflow,
    title: 'Flexible Workflow for Any Team',
    desc: 'Record from Chrome, upload existing videos, edit quickly in the editor, or go through the full AI dashboard workflow depending on your needs.'
  },
  {
    icon: Target,
    title: 'Built for Real Video Use Cases',
    desc: 'Create tutorials, product demos, onboarding guides, educational videos, and training content with a workflow designed for actual production.'
  }
];

function CardRow({ cards }: { cards: typeof topCards }) {
  return (
    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5 md:gap-7">
      {cards.map((card, i) => {
        const Icon = card.icon;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="group"
          >
            <div className="bg-muted/30 dark:bg-muted/20 rounded-xl p-4 sm:p-5 md:p-6 border border-border/50 h-full group-hover:shadow-lg group-hover:-translate-y-1 transition-all duration-300 group-hover:border-primary/30">
              <div className="flex justify-center mb-3 sm:mb-4">
                <div className="w-11 h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-card rounded-xl flex items-center justify-center shadow-sm border border-border/50 group-hover:border-primary/30 transition-colors">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-primary" />
                </div>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-foreground mb-2 sm:mb-3 text-center">
                {card.title}
              </h3>
              <p className="text-muted-foreground text-xs sm:text-sm md:text-base leading-relaxed text-center">
                {card.desc}
              </p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function WhyChoose() {
  return (
    <section className="py-14 sm:py-20 md:py-24 bg-background relative">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="mx-auto flex max-w-5xl flex-col items-center text-center mb-10 sm:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center rounded-full border border-primary/20 px-3 py-1 text-xs font-medium text-primary mb-6 bg-videomule-light-green dark:bg-primary/10">
              Why Choose Us
            </span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground">
              Why Choose VideoMule
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <p className="text-muted-foreground text-sm sm:text-base md:text-lg max-w-3xl mx-auto leading-relaxed mt-3">
              VideoMule brings recording, AI generation, editing, and export
              into one workflow — so you can turn simple screen recordings into
              finished videos without stitching together multiple tools.
            </p>
          </motion.div>
        </div>

        {/* Logo hub */}
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="bg-linear-to-br from-muted to-muted/60 rounded-2xl p-1.5 shadow-xl">
              <div className="rounded-xl px-5 sm:px-8 py-3 sm:py-6 text-center min-w-[160px] sm:min-w-[220px] bg-green-600">
                <img
                  alt="VideoMule"
                  src="/assets/homepage/vmlogo.png"
                  className="h-5 sm:h-8 w-auto mx-auto mb-2 brightness-0 invert"
                />
                <span className="text-primary-foreground/90 font-medium text-xs sm:text-base">
                  Approach
                </span>
              </div>
            </div>
            <div className="absolute left-1/2 -bottom-12 w-px h-12 -translate-x-1/2 overflow-hidden hidden lg:block">
              <div className="w-full h-full bg-linear-to-b from-primary via-border to-border animate-pulse" />
            </div>
          </div>
        </div>

        {/* Branch lines - top */}
        <div className="hidden lg:block relative h-16">
          <svg
            className="w-full h-full"
            viewBox="0 0 1000 60"
            fill="none"
            preserveAspectRatio="none"
          >
            <defs>
              <filter
                id="dotGlowWhy"
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur
                  stdDeviation="3"
                  result="blur"
                />
                <feComposite
                  in="SourceGraphic"
                  in2="blur"
                  operator="over"
                />
              </filter>
              <radialGradient
                id="dotGradientWhy"
                cx="50%"
                cy="50%"
                r="50%"
              >
                <stop
                  offset="0%"
                  stopColor="var(--primary)"
                  stopOpacity="1"
                />
                <stop
                  offset="60%"
                  stopColor="var(--primary)"
                  stopOpacity="0.8"
                />
                <stop
                  offset="100%"
                  stopColor="var(--primary)"
                  stopOpacity="0.4"
                />
              </radialGradient>
            </defs>
            <path
              d="M500 0 L500 20 L167 20 L167 60"
              stroke="var(--border)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M500 20 L500 60"
              stroke="var(--border)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M500 20 L833 20 L833 60"
              stroke="var(--border)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="4s"
                  repeatCount="indefinite"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M500 0 L500 20 L167 20 L167 60"
                />
              </circle>
            </g>
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="3s"
                  repeatCount="indefinite"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M500 0 L500 60"
                />
              </circle>
            </g>
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="4s"
                  repeatCount="indefinite"
                  begin="0.5s"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M500 0 L500 20 L833 20 L833 60"
                />
              </circle>
            </g>
          </svg>
        </div>

        <div className="mt-6 lg:mt-0 mb-4 sm:mb-7 md:mb-0">
          <CardRow cards={topCards} />
        </div>

        {/* Branch lines - middle */}
        <div className="hidden lg:block relative h-12">
          <svg
            className="w-full h-full"
            viewBox="0 0 1000 50"
            fill="none"
            preserveAspectRatio="none"
          >
            <path
              d="M167 0 L167 50"
              stroke="var(--border)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M500 0 L500 50"
              stroke="var(--border)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M833 0 L833 50"
              stroke="var(--border)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="2.5s"
                  repeatCount="indefinite"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M167 0 L167 50"
                />
              </circle>
            </g>
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="2.5s"
                  repeatCount="indefinite"
                  begin="0.4s"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M500 0 L500 50"
                />
              </circle>
            </g>
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="2.5s"
                  repeatCount="indefinite"
                  begin="0.8s"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M833 0 L833 50"
                />
              </circle>
            </g>
          </svg>
        </div>

        <CardRow cards={bottomCards} />

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-8 sm:mt-10 md:mt-12"
        >
          <Link
            href={routes.dashboard.auth.SignUp}
            onClick={() => trackCta('cta_signup_clicked', { surface: 'homepage_why_choose' })}
            className="inline-flex items-center justify-center rounded-full bg-videomule-green text-videomule-white hover:bg-videomule-green-hover h-12 sm:h-14 px-8 sm:px-10 text-sm sm:text-base font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-300"
          >
            Create Your First Video
          </Link>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-border to-transparent" />
    </section>
  );
}
